import { motion, useReducedMotion } from "framer-motion";
import { useId } from "react";

export type IagoState = "idle" | "thinking" | "greeting";

interface Props {
  size?: number;
  state?: IagoState;
  className?: string;
  float?: boolean;
  /** mostra o cone de projeção partindo da base (uso "destacado") */
  projection?: boolean;
  /** multiplicador da opacidade do halo externo (1 = padrão, 0 = desligado) */
  haloOpacity?: number;
  /** mantém uma respiração leve no halo mesmo em idle (1 anim suave, baixo custo) */
  breathe?: boolean;
}

/**
 * IAGO — cérebro neural neon holográfico.
 *
 * Dois hemisférios com silhueta anatômica suave, sulcos curvos imitando
 * giros cerebrais, nós luminosos pulsantes em tamanhos variados e pulsos
 * de luz viajando pelos sulcos (sinapses disparando). Paleta viva da
 * marca: hemisfério esquerdo em fuchsia/violet, direito em sky/violet.
 */

// ═════════ Silhueta do hemisfério esquerdo (viewBox 100x100) ═════════
// Cérebro achatado (formato castanha). Ocupa y: 18-72, x: 7-93 — largura ≈ 1.5x a altura.
// Path: topo → lobo frontal → temporal lateral → base volta pela fissura.
const LEFT_OUTLINE =
  "M 49 18 " +
  "C 42 16 34 17 26 22 " +  // lobo frontal
  "C 18 26 11 34 8 44 " +   // lateral expandindo
  "C 6 50 7 58 12 64 " +    // temporal descendo
  "C 16 70 23 72 30 72 " +  // base externa
  "C 37 73 43 71 46 68 " +  // base curva de volta
  "C 48 62 48 50 48 38 " +  // subida interna (acompanha fissura)
  "C 48 28 49 22 49 18 Z";

const RIGHT_OUTLINE =
  "M 51 18 " +
  "C 58 16 66 17 74 22 " +
  "C 82 26 89 34 92 44 " +
  "C 94 50 93 58 88 64 " +
  "C 84 70 77 72 70 72 " +
  "C 63 73 57 71 54 68 " +
  "C 52 62 52 50 52 38 " +
  "C 52 28 51 22 51 18 Z";

// Sulcos internos — paths curvos imitando giros. Cada entry: [d, strokeWeight]
// Coordenadas dentro do novo contorno (y: 18-72, x: 7-48).
const LEFT_SULCI: [string, number][] = [
  // Giro cingulado (arco superior grande)
  ["M 44 20 C 36 24 28 30 22 38 C 18 44 18 52 22 58", 1],
  // Giro frontal médio
  ["M 38 24 C 30 28 22 34 16 42", 0.85],
  // Sulco central (separa frontal/parietal)
  ["M 30 22 C 28 32 30 40 34 46", 0.85],
  // Sulco lateral principal (horizontal)
  ["M 12 44 C 20 46 30 46 40 44", 0.85],
  // Giro temporal superior
  ["M 10 52 C 18 54 28 54 40 52", 0.85],
  // Giro temporal inferior
  ["M 14 60 C 22 62 32 62 42 60", 0.8],
  // Curva occipital
  ["M 20 66 C 28 68 36 68 44 66", 0.8],
  // Detalhe parietal (vertical curto)
  ["M 34 32 C 32 38 34 44 38 48", 0.75],
  // Curva lateral-temporal
  ["M 18 38 C 22 42 26 46 30 48", 0.65],
  // Conexão interna sutil
  ["M 16 50 C 22 54 28 56 34 56", 0.65],
];

const RIGHT_SULCI: [string, number][] = LEFT_SULCI.map(([d, w]) => [
  mirrorPathX(d, 100),
  w,
]);

// Nós (círculos luminosos). [x, y, radius]. Raios maiores = "hotspots"
// Coordenadas dentro do novo contorno (y: 18-72, x: 7-48).
const LEFT_NODES: [number, number, number][] = [
  // contorno exterior (do topo pela frontal → temporal → base)
  [49, 18, 1.1],
  [42, 18, 0.9],
  [34, 20, 1.2],
  [26, 24, 0.9],
  [18, 30, 0.9],
  [12, 38, 1.1],
  [8, 46, 1.2],
  [9, 54, 0.9],
  [14, 62, 1.0],
  [22, 68, 0.9],
  [30, 71, 1.2],
  [38, 72, 0.9],
  [45, 69, 1.0],
  // interiores nos sulcos
  [30, 30, 0.9],
  [22, 38, 0.8],
  [28, 44, 1.0],
  [34, 40, 0.8],
  [38, 30, 0.9],
  [24, 50, 0.8],
  [32, 52, 1.1],
  [38, 56, 0.9],
  [26, 58, 0.8],
  [32, 62, 0.9],
  [42, 46, 1.1],
  [46, 36, 0.85],
  [47, 26, 0.9],
  [40, 60, 0.8],
];

const RIGHT_NODES: [number, number, number][] = LEFT_NODES.map(([x, y, r]) => [
  100 - x,
  y,
  r,
]);

// Ramificações externas (fios saindo do contorno) — extremos da silhueta nova
const LEFT_BRANCHES: { from: [number, number]; to: [number, number] }[] = [
  { from: [12, 38], to: [3, 32] },
  { from: [8, 46], to: [1, 46] },
  { from: [9, 54], to: [2, 60] },
  { from: [14, 62], to: [6, 72] },
  { from: [22, 68], to: [18, 80] },
  { from: [18, 30], to: [10, 22] },
];

const RIGHT_BRANCHES = LEFT_BRANCHES.map(({ from, to }) => ({
  from: [100 - from[0], from[1]] as [number, number],
  to: [100 - to[0], to[1]] as [number, number],
}));

// Edges pros pulsos "viajantes" — um sulco longo por hemisfério
const LEFT_PULSE_PATH =
  "M 44 20 C 36 24 28 30 22 38 C 18 44 18 52 22 58";
const RIGHT_PULSE_PATH = mirrorPathX(LEFT_PULSE_PATH, 100);

interface HemiTheme {
  outlineGrad: string;
  sulcusColor: string;
  nodeColor: string;
  nodeHotColor: string;
  branchColor: string;
  branchFill: string;
  pulseColor: string;
  glowColor: string;
  delayPhase: number;
}

// ═════════════════════════ Componente ═════════════════════════

export default function IagoAvatar({
  size = 64,
  state = "idle",
  className,
  float = true,
  projection = false,
  haloOpacity = 1,
  breathe = false,
}: Props) {
  const reduce = useReducedMotion();
  const uid = useId().replace(/:/g, "-");

  const idLeftStroke = `iago-lo-${uid}`;
  const idRightStroke = `iago-ro-${uid}`;
  const idLeftGlow = `iago-lg-${uid}`;
  const idRightGlow = `iago-rg-${uid}`;
  const idBaseGrad = `iago-base-${uid}`;

  const animateAll = !reduce && state !== "idle";

  const idleBreathe = breathe && !reduce && state === "idle";

  const haloAnim =
    state === "thinking" && animateAll
      ? { scale: [1, 1.16, 1], opacity: [0.6 * haloOpacity, 0.32 * haloOpacity, 0.6 * haloOpacity] }
      : state === "greeting" && animateAll
        ? { scale: [1, 1.08, 1], opacity: [0.65 * haloOpacity, 0.5 * haloOpacity, 0.65 * haloOpacity] }
        : idleBreathe
          ? { scale: [1, 1.06, 1], opacity: [0.5 * haloOpacity, 0.72 * haloOpacity, 0.5 * haloOpacity] }
          : { scale: 1, opacity: 0.55 * haloOpacity };
  const haloT =
    state === "thinking" && animateAll
      ? { duration: 1.6, repeat: Infinity, ease: "easeInOut" as const }
      : state === "greeting" && animateAll
        ? { duration: 1.4, repeat: Infinity, ease: "easeInOut" as const }
        : idleBreathe
          ? { duration: 5.2, repeat: Infinity, ease: "easeInOut" as const }
          : { duration: 0.6 };

  const flicker =
    animateAll && state !== "thinking"
      ? { opacity: [0.95, 1, 0.98, 1, 0.96, 1] }
      : { opacity: 1 };
  const flickerT = animateAll
    ? {
        duration: 4.4,
        repeat: Infinity,
        ease: "linear" as const,
        times: [0, 0.18, 0.42, 0.6, 0.82, 1],
      }
    : { duration: 0 };

  const floatAnim = float && animateAll ? { y: [0, -3, 0] } : { y: 0 };
  const floatT = float && animateAll
    ? { duration: 5.5, repeat: Infinity, ease: "easeInOut" as const }
    : { duration: 0 };

  const greetPop =
    state === "greeting" && !reduce ? { scale: [0.92, 1.06, 1] } : { scale: 1 };
  const greetT =
    state === "greeting"
      ? { duration: 0.55, ease: "easeOut" as const }
      : { duration: 0 };

  const leftTheme: HemiTheme = {
    outlineGrad: idLeftStroke,
    sulcusColor: "#e879f9", // fuchsia-400
    nodeColor: "#f0abfc", // fuchsia-300
    nodeHotColor: "#fdf4ff", // fuchsia-50
    branchColor: "rgba(232,121,249,0.65)",
    branchFill: "#f0abfc",
    pulseColor: "#fdf4ff",
    glowColor: "rgba(217,70,239,1)",
    delayPhase: 0,
  };
  const rightTheme: HemiTheme = {
    outlineGrad: idRightStroke,
    sulcusColor: "#38bdf8", // sky-400
    nodeColor: "#7dd3fc", // sky-300
    nodeHotColor: "#f0f9ff", // sky-50
    branchColor: "rgba(56,189,248,0.65)",
    branchFill: "#7dd3fc",
    pulseColor: "#f0f9ff",
    glowColor: "rgba(14,165,233,1)",
    delayPhase: 0.4,
  };

  return (
    <motion.div
      className={className}
      style={{
        width: size,
        height: size,
        position: "relative",
        flexShrink: 0,
      }}
      animate={floatAnim}
      transition={floatT}
    >
      {/* halo neon por trás */}
      <motion.div
        aria-hidden
        style={{
          position: "absolute",
          inset: -size * 0.35,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(217,70,239,0.5) 0%, rgba(139,92,246,0.38) 35%, rgba(14,165,233,0.32) 65%, rgba(14,165,233,0) 82%)",
          pointerEvents: "none",
          filter: "blur(6px)",
          mixBlendMode: "screen",
        }}
        animate={haloAnim}
        transition={haloT}
      />

      <motion.div
        style={{
          width: size,
          height: size,
          position: "relative",
          mixBlendMode: "screen",
        }}
        animate={greetPop}
        transition={greetT}
      >
        <motion.svg
          viewBox="0 0 100 100"
          width={size}
          height={size}
          style={{ display: "block", overflow: "visible" }}
          role="img"
          aria-label="IAGO"
          animate={flicker}
          transition={flickerT}
        >
          <defs>
            {/* gradientes neon pros contornos */}
            <linearGradient id={idLeftStroke} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f0abfc" />
              <stop offset="50%" stopColor="#e879f9" />
              <stop offset="100%" stopColor="#a78bfa" />
            </linearGradient>
            <linearGradient id={idRightStroke} x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#7dd3fc" />
              <stop offset="50%" stopColor="#38bdf8" />
              <stop offset="100%" stopColor="#a78bfa" />
            </linearGradient>
            {/* glow interno muito sutil (quase sem fill — edges brilhantes fazem o trabalho) */}
            <radialGradient id={idLeftGlow} cx="45%" cy="45%" r="55%">
              <stop offset="0%" stopColor="rgba(217,70,239,0.18)" />
              <stop offset="100%" stopColor="rgba(217,70,239,0)" />
            </radialGradient>
            <radialGradient id={idRightGlow} cx="55%" cy="45%" r="55%">
              <stop offset="0%" stopColor="rgba(14,165,233,0.18)" />
              <stop offset="100%" stopColor="rgba(14,165,233,0)" />
            </radialGradient>
            <linearGradient id={idBaseGrad} x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor="rgba(139,92,246,0.55)" />
              <stop offset="60%" stopColor="rgba(14,165,233,0.3)" />
              <stop offset="100%" stopColor="rgba(14,165,233,0)" />
            </linearGradient>
          </defs>

          {/* cone de projeção opcional */}
          {projection && (
            <>
              <path
                d="M44 82 L 56 82 L 72 100 L 28 100 Z"
                fill={`url(#${idBaseGrad})`}
                opacity="0.6"
                style={{ filter: "blur(0.6px)" }}
              />
              <line
                x1="50"
                y1="98"
                x2="32"
                y2="82"
                stroke="rgba(217,70,239,0.45)"
                strokeWidth="0.6"
              />
              <line
                x1="50"
                y1="98"
                x2="68"
                y2="82"
                stroke="rgba(125,211,252,0.45)"
                strokeWidth="0.6"
              />
              <circle
                cx="50"
                cy="98"
                r="2.6"
                fill="#f5f3ff"
                style={{ filter: "drop-shadow(0 0 6px rgba(139,92,246,1))" }}
              />
            </>
          )}

          {/* fill interior (quase invisível, só dá volume) */}
          <path d={LEFT_OUTLINE} fill={`url(#${idLeftGlow})`} />
          <path d={RIGHT_OUTLINE} fill={`url(#${idRightGlow})`} />

          {/* ramificações externas (fios saindo) */}
          {renderBranches(LEFT_BRANCHES, leftTheme, "lb", reduce || state === "idle")}
          {renderBranches(RIGHT_BRANCHES, rightTheme, "rb", reduce || state === "idle")}

          {/* sulcos internos */}
          {renderSulci(LEFT_SULCI, leftTheme, "ls")}
          {renderSulci(RIGHT_SULCI, rightTheme, "rs")}

          {/* contorno principal dos hemisférios — edges neon brilhantes */}
          <path
            d={LEFT_OUTLINE}
            fill="none"
            stroke={`url(#${idLeftStroke})`}
            strokeWidth="1.3"
            strokeLinejoin="round"
            style={{ filter: "drop-shadow(0 0 2.5px rgba(217,70,239,0.75))" }}
          />
          <path
            d={RIGHT_OUTLINE}
            fill="none"
            stroke={`url(#${idRightStroke})`}
            strokeWidth="1.3"
            strokeLinejoin="round"
            style={{ filter: "drop-shadow(0 0 2.5px rgba(14,165,233,0.75))" }}
          />

          {/* nós luminosos */}
          {renderNodes(LEFT_NODES, leftTheme, "ln", reduce || state === "idle")}
          {renderNodes(RIGHT_NODES, rightTheme, "rn", reduce || state === "idle")}

          {/* pulsos "sinapses" — luz viajando pelos sulcos longos */}
          {animateAll && (
            <>
              <TravelingPulse
                path={LEFT_PULSE_PATH}
                color={leftTheme.pulseColor}
                shadow={leftTheme.glowColor}
                duration={2.6}
                delay={0}
              />
              <TravelingPulse
                path={RIGHT_PULSE_PATH}
                color={rightTheme.pulseColor}
                shadow={rightTheme.glowColor}
                duration={2.6}
                delay={1.3}
              />
            </>
          )}

          {/* fissura central destacada (com leve glow) */}
          <line
            x1="50"
            y1="20"
            x2="50"
            y2="70"
            stroke="rgba(255,255,255,0.28)"
            strokeWidth="0.6"
            strokeDasharray="1.4 1.4"
            style={{ filter: "drop-shadow(0 0 1.5px rgba(167,139,250,0.5))" }}
          />
        </motion.svg>
      </motion.div>
    </motion.div>
  );
}

// ═════════════════════════ Helpers ═════════════════════════

function renderSulci(
  sulci: [string, number][],
  t: HemiTheme,
  keyPrefix: string,
) {
  return sulci.map(([d, w], i) => (
    <path
      key={`${keyPrefix}-${i}`}
      d={d}
      fill="none"
      stroke={t.sulcusColor}
      strokeWidth={w}
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity={0.75 + (i % 3) * 0.08}
      style={{ filter: `drop-shadow(0 0 1.6px ${t.glowColor})` }}
    />
  ));
}

function renderNodes(
  nodes: [number, number, number][],
  t: HemiTheme,
  keyPrefix: string,
  reduce: boolean | null,
) {
  return nodes.map(([x, y, r], i) => {
    const isHot = r >= 1.1;
    return (
      <motion.circle
        key={`${keyPrefix}-${i}`}
        cx={x}
        cy={y}
        r={r}
        fill={isHot ? t.nodeHotColor : t.nodeColor}
        style={{
          filter: isHot
            ? `drop-shadow(0 0 3.5px ${t.glowColor})`
            : `drop-shadow(0 0 2px ${t.glowColor})`,
        }}
        animate={
          reduce || !isHot
            ? {}
            : { opacity: [0.8, 1, 0.8], r: [r, r * 1.35, r] }
        }
        transition={
          reduce
            ? { duration: 0 }
            : {
                duration: 2 + (i % 4) * 0.3,
                repeat: Infinity,
                ease: "easeInOut" as const,
                delay: (i * 0.15 + t.delayPhase) % 2.4,
              }
        }
      />
    );
  });
}

function renderBranches(
  branches: { from: [number, number]; to: [number, number] }[],
  t: HemiTheme,
  keyPrefix: string,
  reduce: boolean | null,
) {
  return branches.map((b, i) => (
    <g key={`${keyPrefix}-${i}`}>
      <line
        x1={b.from[0]}
        y1={b.from[1]}
        x2={b.to[0]}
        y2={b.to[1]}
        stroke={t.branchColor}
        strokeWidth="0.55"
      />
      <motion.circle
        cx={b.to[0]}
        cy={b.to[1]}
        r="1.1"
        fill={t.branchFill}
        style={{ filter: `drop-shadow(0 0 2.5px ${t.glowColor})` }}
        animate={reduce ? {} : { opacity: [0.5, 1, 0.5], r: [0.9, 1.5, 0.9] }}
        transition={
          reduce
            ? { duration: 0 }
            : {
                duration: 1.6 + i * 0.3,
                repeat: Infinity,
                ease: "easeInOut" as const,
                delay: (i * 0.35 + t.delayPhase) % 2,
              }
        }
      />
    </g>
  ));
}

/** Ponto de luz que viaja ao longo de um path SVG (sinapse disparando). */
function TravelingPulse({
  path,
  color,
  shadow,
  duration,
  delay,
}: {
  path: string;
  color: string;
  shadow: string;
  duration: number;
  delay: number;
}) {
  return (
    <motion.g
      style={{
        filter: `drop-shadow(0 0 4px ${shadow})`,
        offsetPath: `path('${path}')`,
        offsetRotate: "0deg",
      }}
      initial={{ offsetDistance: "0%", opacity: 0 }}
      animate={{
        offsetDistance: ["0%", "100%"],
        opacity: [0, 1, 1, 0],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        repeatDelay: 0.4,
        ease: "easeInOut",
        times: [0, 0.15, 0.85, 1],
      }}
    >
      <circle r="1.6" fill={color} />
    </motion.g>
  );
}

/**
 * Espelha horizontalmente um path SVG simples (só suporta comandos M/L/C/Q/Z
 * com coordenadas absolutas — suficiente pros paths que usamos aqui).
 */
function mirrorPathX(d: string, width: number): string {
  return d.replace(
    /([MLCQ])\s*([-\d.\s,]+)/g,
    (_match, cmd: string, rest: string) => {
      const nums = rest
        .trim()
        .split(/[\s,]+/)
        .map(Number);
      const mirrored = nums.map((n, i) => (i % 2 === 0 ? width - n : n));
      return `${cmd} ${mirrored.join(" ")} `;
    },
  );
}
